import { defaultCode } from './constants';
import { isArgsOutputMode, resolveAssertOn } from './functionModeTypes';

export const isFunctionMode = (problem) => problem?.mode === 'function';

const ALL_FUNCTION_LANGUAGES = [
  { id: 'java', label: 'Java' },
  { id: 'python', label: 'Python' },
  { id: 'cpp', label: 'C++' },
  { id: 'javascript', label: 'JS (Node)' },
  { id: 'csharp', label: 'C#' },
];

/** True when signature uses List/Map/Set/ListNode/TreeNode that need the generic harness. */
export const signatureNeedsAdvancedCodec = (signature) => {
  const types = [
    signature?.returnType,
    ...(signature?.params || []).map((p) => p.type),
  ]
    .filter(Boolean)
    .map((t) => String(t));

  return types.some((t) => {
    if (t === 'ListNode' || t === 'TreeNode') return true;
    if (/^(List|Map|Set)\s*</i.test(t)) return true;
    return false;
  });
};

/** All function-mode languages are available; drivers adapt per-language syntax. */
export const functionModeLanguagesForSignature = (_signature) => ALL_FUNCTION_LANGUAGES;

const isArrayType = (type) => {
  const t = String(type || '');
  return t.endsWith('[]') && !t.endsWith('[][]');
};
const isMatrixType = (type) => String(type || '').endsWith('[][]');
const isNestedListType = (type) => /^List\s*<\s*List\s*</i.test(String(type || ''));
const isListNode = (type) => type === 'ListNode' || type === 'TreeNode';
const isListGenericType = (type) => /^List\s*</i.test(String(type || '').trim());
const isMapGenericType = (type) => /^Map\s*</i.test(String(type || '').trim());
const isSetGenericType = (type) => /^Set\s*</i.test(String(type || '').trim());
/** Types that store nested arrays — still editable as a single simple field. */
const isMatrixLikeType = (type) => isMatrixType(type) || isNestedListType(type);
const isJsonPreferredType = (type) =>
  isMapGenericType(type) ||
  isSetGenericType(type);

const elementTypeOf = (type) => {
  const t = String(type || '').trim();
  const listMatch = t.match(/^List\s*<\s*(.+)\s*>$/i);
  if (listMatch) return listMatch[1].trim();
  const setMatch = t.match(/^Set\s*<\s*(.+)\s*>$/i);
  if (setMatch) return setMatch[1].trim();
  if (isMatrixType(t)) return t.slice(0, -2); // int[][] → int[]
  if (isArrayType(t)) return t.slice(0, -2);
  return null;
};

const splitCommaParts = (text) => {
  const parts = String(text).split(',').map((p) => p.trim());
  while (parts.length > 0 && parts[parts.length - 1] === '') {
    parts.pop();
  }
  return parts;
};

const parseScalarToken = (elemType, raw) => {
  const text = String(raw ?? '').trim();
  if (!text || text === 'null') return null;
  if (elemType === 'bool') return text === 'true' || text === '1';
  if (elemType === 'string' || elemType === 'char') return text.replace(/^"|"$/g, '');
  if (elemType === 'float' || elemType === 'double') return parseFloat(text);
  if (elemType === 'int' || elemType === 'long') return parseInt(text, 10);
  if (text.includes('.')) return parseFloat(text);
  if (text === 'true' || text === 'false') return text === 'true';
  const asNum = Number(text);
  return Number.isNaN(asNum) ? text.replace(/^"|"$/g, '') : asNum;
};

const parseCommaSeparatedTyped = (elemType, text) =>
  splitCommaParts(text)
    .filter((p) => p !== '')
    .map((p) => parseScalarToken(elemType, p));

const tryParseJson = (text) => {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
};

/** Parse matrix as JSON only: [[1,2],[3,4]] */
const parseMatrixLike = (type, text) => {
  const trimmed = String(text ?? '').trim();
  if (!trimmed) return null;
  const parsed = tryParseJson(trimmed);
  return Array.isArray(parsed) ? parsed : null;
};

const formatListDisplay = (items) =>
  items
    .map((v) => {
      if (v == null) return 'null';
      if (typeof v === 'string') return v;
      if (typeof v === 'object') return JSON.stringify(v);
      return String(v);
    })
    .join(', ');

const formatMatrixLike = (value) => JSON.stringify(value);

/** Parse student/admin-friendly text into typed JSON values. */
export const parseFieldValue = (type, raw) => {
  const text = String(raw ?? '').trim();
  if (!text) {
    if (type === 'string') return '';
    // Empty ListNode / TreeNode → empty structure (LeetCode [])
    if (type === 'ListNode' || type === 'TreeNode') return [];
    if (isMatrixLikeType(type)) return [];
    return null;
  }

  if (type === 'bool') return text === 'true' || text === '1';
  if (type === 'int' || type === 'long') return parseInt(text, 10);
  if (type === 'float' || type === 'double') return parseFloat(text);
  if (type === 'string' || type === 'char') return text.replace(/^"|"$/g, '');

  if (isMatrixLikeType(type)) {
    return parseMatrixLike(type, text);
  }

  // Prefer JSON for maps/sets when text looks like JSON.
  if (text.startsWith('[') || text.startsWith('{')) {
    const parsed = tryParseJson(text);
    if (parsed !== undefined) return parsed;
    // Incomplete JSON while typing — do not store as a raw string.
    if (isJsonPreferredType(type) || isMapGenericType(type) || isSetGenericType(type)) {
      return null;
    }
  }

  if (isMapGenericType(type)) {
    const parsed = tryParseJson(text);
    return parsed !== undefined && parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed
      : null;
  }

  if (isSetGenericType(type)) {
    const parsed = tryParseJson(text);
    if (Array.isArray(parsed)) return parsed;
    const elem = elementTypeOf(type) || 'int';
    // Allow comma lists for scalar sets: 1, 2, 3
    if (!text.startsWith('{') && !text.startsWith('[')) {
      return parseCommaSeparatedTyped(elem, text);
    }
    return null;
  }

  if (type === 'TreeNode') {
    return splitCommaParts(text).map((v) => {
      if (v === 'null' || v === '') return null;
      return Number(v);
    });
  }

  if (isListNode(type) || isArrayType(type) || isListGenericType(type)) {
    const elem = elementTypeOf(type) || (isListNode(type) ? 'int' : 'int');
    // Nested List<Map<...>> should be JSON
    if (/^List\s*<\s*Map\s*</i.test(String(type || ''))) {
      const parsed = tryParseJson(text);
      return parsed !== undefined ? parsed : null;
    }
    return parseCommaSeparatedTyped(elem, text);
  }

  return text;
};

export const formatFieldValue = (type, value) => {
  if (value == null) return '';

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (
      (trimmed.startsWith('[') || trimmed.startsWith('{')) &&
      (isListGenericType(type) ||
        isArrayType(type) ||
        isMatrixType(type) ||
        isMapGenericType(type) ||
        isSetGenericType(type) ||
        isListNode(type))
    ) {
      const parsed = tryParseJson(trimmed);
      if (parsed !== undefined) return formatFieldValue(type, parsed);
    }
    return value;
  }

  if (Array.isArray(value)) {
    if (isMatrixLikeType(type)) {
      return formatMatrixLike(value);
    }
    if (isSetGenericType(type)) {
      return JSON.stringify(value);
    }
    if (isArrayType(type) || isListGenericType(type) || isListNode(type) || type === 'TreeNode') {
      // Nested objects/arrays → JSON for safety
      if (value.some((v) => v && typeof v === 'object')) return JSON.stringify(value);
      return formatListDisplay(value);
    }
  }

  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

export const fieldHelperForType = (type) => {
  if (type === 'ListNode') {
    return 'Linked list as values: 1, 2, 3  (empty: leave blank)';
  }
  if (type === 'TreeNode') {
    return 'Level-order with nulls: 3, 9, 20, null, null, 15, 7';
  }
  if (isMapGenericType(type)) return 'JSON object: {"1":2,"3":4}';
  if (isSetGenericType(type)) return 'Comma-separated or JSON array: 1, 2, 3';
  if (isMatrixLikeType(type)) {
    return 'JSON matrix: [[1,2],[3,4]]';
  }
  if (isArrayType(type) || isListGenericType(type)) {
    const elem = elementTypeOf(type);
    if (elem === 'string' || elem === 'char') return 'Comma-separated: a, b, c  (or JSON array)';
    if (elem === 'bool') return 'Comma-separated: true, false, true';
    return 'Comma-separated: 2, 7, 11, 15';
  }
  if (type === 'bool') return 'true or false';
  if (type === 'char') return 'Single character: a';
  return '';
};

/** Apply a parsed field value onto an args/expected object (removes cleared keys). */
export const applyParsedField = (container, name, type, parsed) => {
  const next = { ...(container || {}) };
  const empty =
    parsed === null ||
    parsed === undefined ||
    (parsed === '' && type === 'string');
  if (empty && type !== 'string') {
    delete next[name];
  } else if (parsed === '' && type === 'string') {
    next[name] = '';
  } else {
    next[name] = parsed;
  }
  return next;
};

export const getSignatureParamNames = (signature) =>
  new Set(
    (signature?.params || [])
      .map((p) => String(p.name || '').trim())
      .filter(Boolean),
  );

/** Drop args/expected keys that are no longer in the method signature. */
export const pruneTestCasesForSignature = (testCases, signature) => {
  const paramNames = getSignatureParamNames(signature);
  const argsMode = isArgsOutputMode(signature);
  const allowedExpectedKeys = argsMode
    ? new Set(resolveAssertOn(signature).filter((n) => paramNames.has(n)))
    : null;

  return (testCases || []).map((tc) => {
    let args = tc.args;
    if (args && typeof args === 'object' && !Array.isArray(args)) {
      args = Object.fromEntries(Object.entries(args).filter(([key]) => paramNames.has(key)));
    }

    let expected = tc.expected;
    if (
      argsMode &&
      expected &&
      typeof expected === 'object' &&
      !Array.isArray(expected)
    ) {
      expected = Object.fromEntries(
        Object.entries(expected).filter(([key]) => allowedExpectedKeys.has(key)),
      );
    }

    if (args === tc.args && expected === tc.expected) return tc;
    return { ...tc, args, expected };
  });
};

export const pruneSignatureAssertOn = (signature) => {
  if (!signature || !Array.isArray(signature.assertOn)) return signature;
  const paramNames = getSignatureParamNames(signature);
  const assertOn = signature.assertOn.filter((n) => paramNames.has(String(n).trim()));
  if (assertOn.length === signature.assertOn.length) return signature;
  return { ...signature, assertOn };
};

export const getEffectiveCaseData = (sampleCase, caseOverride) => {
  if (!caseOverride) {
    return {
      args: sampleCase?.args ?? {},
      expected: sampleCase?.expected,
    };
  }

  return {
    args: caseOverride.args ?? sampleCase?.args ?? {},
    expected:
      caseOverride.expected !== undefined
        ? caseOverride.expected
        : sampleCase?.expected,
  };
};

export const getStarterCode = (problem, lang) => {
  if (isFunctionMode(problem)) {
    return (
      problem?.starterCode?.[lang] ||
      problem?.wrapperCode?.[lang] ||
      defaultCode[lang]
    );
  }
  return problem?.wrapperCode?.[lang] || defaultCode[lang];
};

export const formatValueDisplay = (value) => {
  if (typeof value === 'string') return JSON.stringify(value);
  if (Array.isArray(value)) {
    return `[${value.map(formatValueDisplay).join(',')}]`;
  }
  if (value && typeof value === 'object') {
    return `{${Object.entries(value)
      .map(([k, v]) => `${k}: ${formatValueDisplay(v)}`)
      .join(', ')}}`;
  }
  return String(value);
};

export const formatArgsDisplay = (args) => {
  if (!args || typeof args !== 'object') return '';
  return Object.entries(args)
    .map(([key, value]) => `${key} = ${formatValueDisplay(value)}`)
    .join(', ');
};

export const getSampleDisplay = (sample, signature) => {
  if (sample?.args != null) {
    const argsMode = signature && isArgsOutputMode(signature);
    const expectedDisplay =
      argsMode &&
      sample.expected &&
      typeof sample.expected === 'object' &&
      !Array.isArray(sample.expected)
        ? formatArgsDisplay(sample.expected)
        : formatValueDisplay(sample.expected);
    return {
      input: formatArgsDisplay(sample.args),
      output: expectedDisplay,
      rawArgs: sample.args,
      rawExpected: sample.expected,
    };
  }
  return {
    input: sample?.input ?? '',
    output: sample?.output ?? '',
    rawArgs: null,
    rawExpected: null,
  };
};

/**
 * Build Run request testCase + detect if expected comparison applies.
 * @param {boolean} [useCustomInput] — when true (Custom chip), skip expected compare for IO.
 */
export const buildRunTestCase = ({
  problem,
  sampleCase,
  customInput,
  caseOverride,
  useCustomInput = false,
}) => {
  if (!isFunctionMode(problem)) {
    const sampleInput = String(sampleCase?.input ?? '');
    const rawCustom = String(customInput ?? '');
    if (useCustomInput) {
      return {
        testCase: {
          input: rawCustom,
          output: null,
        },
        usingSampleInput: false,
      };
    }
    // Empty textarea → run the selected sample (with expected). Edited text → custom run (no WA).
    const inputToUse = rawCustom.length > 0 ? rawCustom : sampleInput;
    const usingSampleInput =
      Boolean(sampleCase) &&
      (rawCustom.length === 0 || rawCustom === sampleInput);
    return {
      testCase: {
        input: inputToUse,
        output: usingSampleInput ? sampleCase.output : null,
      },
      usingSampleInput,
    };
  }

  const trimmed = String(customInput ?? '').trim();
  // Custom chip: use typed param override (same shape as Case 1/2/3), not JSON textarea.
  if (useCustomInput) {
    const args = caseOverride?.args;
    if (!args || typeof args !== 'object' || Array.isArray(args)) {
      throw new Error('Fill in the parameter fields before running a custom case.');
    }
    const paramNames = (problem?.signature?.params || []).map((p) => p.name).filter(Boolean);
    const missing = paramNames.filter(
      (name) => args[name] === undefined || args[name] === null,
    );
    if (missing.length > 0) {
      throw new Error(`Missing values for: ${missing.join(', ')}`);
    }
    return {
      testCase: { args, expected: undefined },
      usingSampleInput: false,
    };
  }

  if (trimmed) {
    try {
      const parsed = JSON.parse(trimmed);
      const args = parsed?.args ?? parsed;
      const expected = parsed?.expected;
      return {
        testCase: { args, expected: expected ?? undefined },
        usingSampleInput: false,
      };
    } catch {
      throw new Error('Custom input must be JSON args, e.g. {"nums":[2,7],"target":9}');
    }
  }

  const { args, expected } = getEffectiveCaseData(sampleCase, caseOverride);
  if (!args || typeof args !== 'object') {
    throw new Error('No sample test case available for this problem.');
  }

  return {
    testCase: { args, expected },
    usingSampleInput: !caseOverride,
  };
};

/**
 * Build run payloads for every sample case + every custom case (for multi-case Run).
 * Incomplete custom cases are skipped (not thrown).
 */
export const buildAllRunTestCases = ({
  problem,
  sampleCases = [],
  customCases = [],
  functionCaseOverrides = {},
  selectedSampleIndex = 0,
  sampleInputOverride = '',
}) => {
  const jobs = [];

  (sampleCases || []).forEach((sample, index) => {
    try {
      const editedInput =
        !isFunctionMode(problem) &&
        index === selectedSampleIndex &&
        String(sampleInputOverride ?? '').length > 0
          ? sampleInputOverride
          : undefined;

      const payload = buildRunTestCase({
        problem,
        sampleCase: sample,
        customInput: isFunctionMode(problem) ? '' : editedInput ?? sample?.input ?? '',
        caseOverride: functionCaseOverrides[index],
        useCustomInput: false,
      });
      jobs.push({
        key: `sample-${index}`,
        label: `Case ${index + 1}`,
        testCase: payload.testCase,
      });
    } catch {
      /* skip malformed sample */
    }
  });

  (customCases || []).forEach((custom, index) => {
    try {
      const payload = buildRunTestCase({
        problem,
        sampleCase: null,
        customInput: custom?.input ?? '',
        caseOverride: { args: custom?.args || {} },
        useCustomInput: true,
      });
      // Skip empty IO custom
      if (
        !isFunctionMode(problem) &&
        !String(payload.testCase?.input ?? '').length
      ) {
        return;
      }
      jobs.push({
        key: `custom-${custom?.id || index}`,
        label: `Custom ${index + 1}`,
        testCase: payload.testCase,
      });
    } catch {
      /* skip incomplete custom */
    }
  });

  return jobs;
};

export const functionModeInputPlaceholder =
  '{"nums":[2,7,11,15],"target":9}  — optional JSON args';
